import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTeacherClasses,
  getMyCustomQuizzes,
  getCustomQuizById,
  updateCustomQuiz,
  deleteCustomQuiz,
} from "../../api/client";
import LiveQuizTab from "../../components/liveQuiz/LiveQuizTab";
import { useTranslation } from "../../hooks/useTranslation";
import { useLanguage } from "../../contexts/LanguageContext";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import { Settings, Plus, BookOpen, Gamepad2, Trash2, Pencil } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://app.fintogo.ai/api";

// ─── Fragen-Formular ──────────────────────────────────────────────────────
function QuestionForm({ index, question, onChange, onRemove, t }) {
  const handleOptionChange = (optIdx, value) => {
    const newOptions = [...question.options];
    newOptions[optIdx] = { ...newOptions[optIdx], text: value };
    onChange(index, { ...question, options: newOptions });
  };

  const handleCorrectChange = (optIdx) => {
    const newOptions = question.options.map((o, i) => ({ ...o, correct: i === optIdx }));
    onChange(index, { ...question, options: newOptions });
  };

  return (
    <div className="card border mb-3">
      <div className="card-body py-2 px-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <span className="fw-semibold small text-muted">
            {t.liveQuiz.question || "Frage"} {index + 1}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger p-1"
            onClick={() => onRemove(index)}
            title={t.common.delete || "Löschen"}
          >
            <Trash2 size={14} />
          </button>
        </div>
        <input
          className="form-control form-control-sm mb-2"
          placeholder={t.liveQuiz.questionPlaceholder || "Frage eingeben..."}
          value={question.text}
          onChange={(e) => onChange(index, { ...question, text: e.target.value })}
        />
        <div className="row g-1">
          {question.options.map((opt, optIdx) => (
            <div className="col-12 col-sm-6" key={optIdx}>
              <div className={`input-group input-group-sm ${
                opt.correct ? "border border-success rounded" : ""
              }`}>
                <span
                  className={`input-group-text ${
                    opt.correct ? "bg-success text-white" : "bg-light"
                  }`}
                  style={{ cursor: "pointer", minWidth: "32px", justifyContent: "center" }}
                  onClick={() => handleCorrectChange(optIdx)}
                  title={t.liveQuiz.markCorrect || "Als richtig markieren"}
                >
                  {opt.correct ? "✓" : String.fromCharCode(65 + optIdx)}
                </span>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={`${t.liveQuiz.option || "Option"} ${String.fromCharCode(65 + optIdx)}`}
                  value={opt.text}
                  onChange={(e) => handleOptionChange(optIdx, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        <small className="text-muted d-block mt-1">
          {t.liveQuiz.clickToMarkCorrect || "Auf den Buchstaben klicken um die richtige Antwort zu markieren"}
        </small>
      </div>
    </div>
  );
}

function createEmptyQuestion() {
  return {
    text: "",
    options: [
      { text: "", correct: true },
      { text: "", correct: false },
      { text: "", correct: false },
      { text: "", correct: false },
    ],
  };
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────
export default function LiveQuizPage() {
  const { locale } = useLanguage();
  const [teacherClasses, setTeacherClasses] = useState([]);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Klassenraum-Verwaltung Modal
  const [showManageModal, setShowManageModal] = useState(false);
  const [customQuizzes, setCustomQuizzes] = useState([]);
  const [customQuizzesLoading, setCustomQuizzesLoading] = useState(false);
  const [customQuizzesError, setCustomQuizzesError] = useState(null);

  // Neuen Klassenraum Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newQuizTitle, setNewQuizTitle] = useState("");
  const [newQuizQuestions, setNewQuizQuestions] = useState([createEmptyQuestion()]);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editQuiz, setEditQuiz] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(false);

  // Delete
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    getTeacherClasses()
      .then((data) => setTeacherClasses(Array.isArray(data) ? data : []))
      .catch(() => setTeacherClasses([]));
  }, []);

  const loadCustomQuizzes = async () => {
    setCustomQuizzesLoading(true);
    setCustomQuizzesError(null);
    try {
      const data = await getMyCustomQuizzes();
      setCustomQuizzes(Array.isArray(data) ? data : []);
    } catch (e) {
      setCustomQuizzesError(
        t.liveQuiz.errorLoadingCustomQuizzes || "Fehler beim Laden der eigenen Quizzes."
      );
      setCustomQuizzes([]);
    } finally {
      setCustomQuizzesLoading(false);
    }
  };

  const handleOpenManageModal = async () => {
    setShowManageModal(true);
    await loadCustomQuizzes();
  };

  const handleOpenCreateModal = () => {
    setNewQuizTitle("");
    setNewQuizQuestions([createEmptyQuestion()]);
    setCreateError(null);
    setCreateSuccess(false);
    setShowManageModal(false);
    setShowCreateModal(true);
  };

  const handleAddQuestion = () => {
    setNewQuizQuestions((prev) => [...prev, createEmptyQuestion()]);
  };

  const handleQuestionChange = (idx, updated) => {
    setNewQuizQuestions((prev) => prev.map((q, i) => (i === idx ? updated : q)));
  };

  const handleRemoveQuestion = (idx) => {
    setNewQuizQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveNewQuiz = async () => {
    if (!newQuizTitle.trim()) {
      setCreateError(t.liveQuiz.titleRequired || "Bitte einen Titel eingeben.");
      return;
    }
    if (newQuizQuestions.length === 0) {
      setCreateError(t.liveQuiz.questionsRequired || "Bitte mindestens eine Frage hinzufügen.");
      return;
    }
    for (let i = 0; i < newQuizQuestions.length; i++) {
      const q = newQuizQuestions[i];
      if (!q.text.trim()) {
        setCreateError(`${t.liveQuiz.question || "Frage"} ${i + 1}: ${t.liveQuiz.textRequired || "Fragetext ist erforderlich."}`);
        return;
      }
      if (!q.options.some((o) => o.correct)) {
        setCreateError(`${t.liveQuiz.question || "Frage"} ${i + 1}: ${t.liveQuiz.correctRequired || "Bitte eine richtige Antwort markieren."}`);
        return;
      }
      if (!q.options.every((o) => o.text.trim())) {
        setCreateError(`${t.liveQuiz.question || "Frage"} ${i + 1}: ${t.liveQuiz.optionsFilled || "Alle Antwortoptionen müssen ausgefüllt sein."}`);
        return;
      }
    }

    setCreateSaving(true);
    setCreateError(null);
    try {
      const payload = {
        title: newQuizTitle.trim(),
        questions: newQuizQuestions.map((q) => ({
          text: q.text.trim(),
          options: q.options.map((o) => ({ text: o.text.trim(), correct: o.correct })),
        })),
      };

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/livequiz/custom-quizzes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Server-Fehler (${res.status}): Ungültige Antwort vom Server.`);
      }

      if (!res.ok) {
        throw new Error(data.error || data.message || `Fehler ${res.status}`);
      }

      setCreateSuccess(true);
      await loadCustomQuizzes();
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateSaving(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setCreateSuccess(false);
  };

  // ─── Edit Handlers ─────────────────────────────────────────────────────────────
  const handleOpenEditModal = async (quizId) => {
    setEditError(null);
    setEditSuccess(false);
    setEditQuiz(null);
    setShowEditModal(true);
    setEditLoading(true);
    try {
      const data = await getCustomQuizById(quizId);
      setEditQuiz({
        id: data.id,
        title: data.title,
        questions: data.questions.map(q => ({
          text: q.text || q.question_text || "",
          options: (q.options || []).map(o => ({
            text: o.text || o.answer_text || "",
            correct: !!o.correct,
          })),
        })),
      });
    } catch (e) {
      setEditError(`Fehler beim Laden: ${e.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditQuestionChange = (idx, updated) => {
    setEditQuiz(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === idx ? updated : q)),
    }));
  };

  const handleEditRemoveQuestion = (idx) => {
    setEditQuiz(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  const handleEditAddQuestion = () => {
    setEditQuiz(prev => ({
      ...prev,
      questions: [...prev.questions, createEmptyQuestion()],
    }));
  };

  const handleSaveEdit = async () => {
    if (!editQuiz) return;
    if (!editQuiz.title.trim()) {
      setEditError(t.liveQuiz.titleRequired || "Bitte einen Titel eingeben.");
      return;
    }
    if (editQuiz.questions.length === 0) {
      setEditError(t.liveQuiz.questionsRequired || "Bitte mindestens eine Frage hinzufügen.");
      return;
    }
    for (let i = 0; i < editQuiz.questions.length; i++) {
      const q = editQuiz.questions[i];
      if (!q.text.trim()) {
        setEditError(`${t.liveQuiz.question || "Frage"} ${i + 1}: ${t.liveQuiz.textRequired || "Fragetext ist erforderlich."}`);
        return;
      }
      if (!q.options.some(o => o.correct)) {
        setEditError(`${t.liveQuiz.question || "Frage"} ${i + 1}: ${t.liveQuiz.correctRequired || "Bitte eine richtige Antwort markieren."}`);
        return;
      }
      if (!q.options.every(o => o.text.trim())) {
        setEditError(`${t.liveQuiz.question || "Frage"} ${i + 1}: ${t.liveQuiz.optionsFilled || "Alle Antwortoptionen müssen ausgefüllt sein."}`);
        return;
      }
    }

    setEditSaving(true);
    setEditError(null);
    try {
      await updateCustomQuiz(editQuiz.id, {
        title: editQuiz.title.trim(),
        questions: editQuiz.questions.map(q => ({
          text: q.text.trim(),
          options: q.options.map(o => ({ text: o.text.trim(), correct: o.correct })),
        })),
      });
      setEditSuccess(true);
      await loadCustomQuizzes();
    } catch (e) {
      setEditError(`Fehler beim Speichern: ${e.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditQuiz(null);
    setEditError(null);
    setEditSuccess(false);
  };

  // ─── Delete Handler ────────────────────────────────────────────────────────────
  const handleDeleteQuiz = async (quizId, quizTitle) => {
    if (!window.confirm(`Quiz "${quizTitle}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setDeletingId(quizId);
    try {
      await deleteCustomQuiz(quizId);
      await loadCustomQuizzes();
    } catch (e) {
      alert(`Fehler beim Löschen: ${e.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main style={{ backgroundColor: "var(--bg-secondary)", minHeight: "100vh" }}>
      <div className="container-fluid py-4 px-3 px-md-4">

        {/* Header */}
        <div className="mb-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3">
          <div>
            <h1 className="mb-1">{t.nav.liveQuiz}</h1>
          </div>
          <Button
            variant="outline-primary"
            className="d-flex align-items-center gap-2 flex-shrink-0"
            onClick={handleOpenManageModal}
          >
            <Settings size={18} />
            <span className="d-none d-sm-inline">{t.liveQuiz.manageClassroomsBtn || "Klassenräume verwalten"}</span>
            <span className="d-sm-none">{t.liveQuiz.manageShort || "Verwalten"}</span>
          </Button>
        </div>

        {/* LiveQuizTab direkt rendern */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body p-3">
            <LiveQuizTab
              classes={teacherClasses}
              lang={locale}
            />
          </div>
        </div>

      </div>

      {/* ===== KLASSENRAUM VERWALTUNG MODAL ===== */}
      <Modal show={showManageModal} onHide={() => setShowManageModal(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Gamepad2 size={22} className="text-primary" />
            {t.liveQuiz.manageClassrooms || "fintogo Klassenräume verwalten"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-4">
            {t.liveQuiz.manageClassroomsSubtitle || "Erstelle neue Live Quiz Klassenräume oder verwalte bestehende"}
          </p>

          {/* Neuen Klassenraum erstellen */}
          <div className="card border-0 bg-light mb-4">
            <div className="card-body">
              <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <Plus size={18} className="text-primary" />
                {t.liveQuiz.newClassroomSection || "Neuer Klassenraum"}
              </h6>
              <p className="text-muted small mb-3">
                {t.liveQuiz.newClassroomDesc || "Erstelle einen neuen Live Quiz Klassenraum mit eigenen Fragen"}
              </p>
              <Button variant="primary" className="d-flex align-items-center gap-2" onClick={handleOpenCreateModal}>
                <Plus size={16} />
                {t.liveQuiz.createNewClassroom || "Neuer Klassenraum erstellen"}
              </Button>
            </div>
          </div>

          {/* Eigene Quizzes */}
          <div>
            <h6 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <Gamepad2 size={18} className="text-primary" />
              {t.liveQuiz.customQuizzes || "Eigene Quizzes"}
            </h6>
            <p className="text-muted small mb-3">
              {t.liveQuiz.customQuizzesSubtitle || "Deine selbst erstellten Quizzes"}
            </p>

            {customQuizzesLoading && (
              <div className="text-center py-4">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                <span className="text-muted small">{t.liveQuiz.loadingCustomQuizzes || "Lädt..."}</span>
              </div>
            )}
            {customQuizzesError && (
              <div className="alert alert-danger py-2 small">{customQuizzesError}</div>
            )}
            {!customQuizzesLoading && !customQuizzesError && customQuizzes.length === 0 && (
              <div className="text-center py-4">
                <Gamepad2 size={40} className="text-muted mb-2" />
                <p className="text-muted small mb-0">
                  {t.liveQuiz.noCustomQuizzes || "Du hast noch keine eigenen Quizzes erstellt."}
                </p>
              </div>
            )}
            {!customQuizzesLoading && customQuizzes.length > 0 && (
              <div className="row g-2">
                {customQuizzes.map((quiz) => (
                  <div className="col-12 col-sm-6" key={quiz.id}>
                    <div className="card border h-100">
                      <div className="card-body py-2 px-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 className="fw-semibold mb-1 small">{quiz.title || `Quiz #${quiz.id}`}</h6>
                          <Badge bg="secondary" className="ms-2">
                            {quiz.questionCount ?? quiz.question_count ?? "?"} Fragen
                          </Badge>
                        </div>
                        {quiz.createdAt && (
                          <small className="text-muted d-block mb-2">
                            {new Date(quiz.createdAt).toLocaleDateString(locale === "en" ? "en-GB" : "de-DE")}
                          </small>
                        )}
                        <div className="d-flex gap-2 mt-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="d-flex align-items-center gap-1"
                            onClick={() => handleOpenEditModal(quiz.id)}
                            title="Bearbeiten"
                          >
                            <Pencil size={13} />
                            <span className="d-none d-sm-inline">Bearbeiten</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="d-flex align-items-center gap-1"
                            onClick={() => handleDeleteQuiz(quiz.id, quiz.title)}
                            disabled={deletingId === quiz.id}
                            title="Löschen"
                          >
                            {deletingId === quiz.id ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <Trash2 size={13} />
                            )}
                            <span className="d-none d-sm-inline">Löschen</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManageModal(false)}>
            {t.common.close || "Schließen"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ===== NEUEN KLASSENRAUM ERSTELLEN MODAL ===== */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Plus size={22} className="text-primary" />
            {t.liveQuiz.createClassroomTitle || "Neuen Klassenraum (Live Quiz) erstellen"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createSuccess ? (
            <div className="text-center py-5">
              <div className="mb-3" style={{ fontSize: "3rem" }}>✅</div>
              <h5 className="fw-bold text-success mb-2">
                {t.liveQuiz.quizSavedSuccess || "Quiz erfolgreich gespeichert!"}
              </h5>
              <p className="text-muted small mb-4">
                {t.liveQuiz.quizSavedSuccessDesc || "Dein Quiz wurde gespeichert und steht jetzt unter \"Eigene Quizzes\" zur Verfügung."}
              </p>
              <div className="d-flex gap-2 justify-content-center flex-wrap">
                <Button variant="primary" onClick={() => { handleCloseCreateModal(); setShowManageModal(false); }}>
                  {t.common.close || "Schließen"}
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setNewQuizTitle("");
                    setNewQuizQuestions([createEmptyQuestion()]);
                    setCreateError(null);
                    setCreateSuccess(false);
                  }}
                >
                  {t.liveQuiz.createAnother || "Weiteres Quiz erstellen"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {createError && (
                <div className="alert alert-danger py-2 small mb-3">{createError}</div>
              )}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  {t.liveQuiz.quizTitle || "Quiz-Titel"}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder={t.liveQuiz.quizTitlePlaceholder || "z.B. Buchführung Grundlagen"}
                  value={newQuizTitle}
                  onChange={(e) => setNewQuizTitle(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-semibold mb-0">
                    Fragen
                    <Badge bg="primary" className="ms-2">{newQuizQuestions.length}</Badge>
                  </label>
                  <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1" onClick={handleAddQuestion}>
                    <Plus size={14} />
                    {t.liveQuiz.addQuestion || "Frage hinzufügen"}
                  </Button>
                </div>
                {newQuizQuestions.length === 0 && (
                  <div className="text-center py-4 text-muted border rounded">
                    <p className="mb-2 small">{t.liveQuiz.noQuestionsYet || "Noch keine Fragen hinzugefügt."}</p>
                    <Button variant="outline-primary" size="sm" onClick={handleAddQuestion}>
                      <Plus size={14} className="me-1" />
                      {t.liveQuiz.addFirstQuestion || "Erste Frage hinzufügen"}
                    </Button>
                  </div>
                )}
                {newQuizQuestions.map((q, idx) => (
                  <QuestionForm
                    key={idx}
                    index={idx}
                    question={q}
                    onChange={handleQuestionChange}
                    onRemove={handleRemoveQuestion}
                    t={t}
                  />
                ))}
              </div>
              <p className="text-muted small">
                <BookOpen size={14} className="me-1" />
                {t.liveQuiz.saveHint || "Nach dem Speichern steht das Quiz unter \"Eigene Quizzes\" zur Verfügung."}
              </p>
            </>
          )}
        </Modal.Body>
        {!createSuccess && (
          <Modal.Footer className="flex-column flex-sm-row gap-2">
            <Button variant="secondary" onClick={handleCloseCreateModal} disabled={createSaving} className="order-2 order-sm-1">
              {t.common.cancel || "Abbrechen"}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveNewQuiz}
              disabled={createSaving || !newQuizTitle.trim() || newQuizQuestions.length === 0}
              className="order-1 order-sm-2 d-flex align-items-center gap-2"
            >
              {createSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  {t.liveQuiz.saving || "Speichert..."}
                </>
              ) : (
                <>
                  <BookOpen size={16} />
                  {t.liveQuiz.saveQuiz || "Quiz speichern"}
                </>
              )}
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* ===== QUIZ BEARBEITEN MODAL ===== */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Pencil size={22} className="text-primary" />
            {"Quiz bearbeiten"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="text-muted mt-2 small">Laden...</p>
            </div>
          )}
          {editSuccess ? (
            <div className="text-center py-5">
              <div className="mb-3" style={{ fontSize: "3rem" }}>✅</div>
              <h5 className="fw-bold text-success mb-2">Quiz erfolgreich aktualisiert!</h5>
              <Button variant="primary" onClick={handleCloseEditModal}>
                {t.common.close || "Schließen"}
              </Button>
            </div>
          ) : editQuiz && !editLoading ? (
            <>
              {editError && (
                <div className="alert alert-danger py-2 small mb-3">{editError}</div>
              )}
              <div className="mb-4">
                <label className="form-label fw-semibold">
                  {t.liveQuiz.quizTitle || "Quiz-Titel"}
                  <span className="text-danger ms-1">*</span>
                </label>
                <input
                  className="form-control"
                  value={editQuiz.title}
                  onChange={(e) => setEditQuiz(prev => ({ ...prev, title: e.target.value }))}
                  maxLength={100}
                />
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-semibold mb-0">
                    Fragen
                    <Badge bg="primary" className="ms-2">{editQuiz.questions.length}</Badge>
                  </label>
                  <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1" onClick={handleEditAddQuestion}>
                    <Plus size={14} />
                    {t.liveQuiz.addQuestion || "Frage hinzufügen"}
                  </Button>
                </div>
                {editQuiz.questions.map((q, idx) => (
                  <QuestionForm
                    key={idx}
                    index={idx}
                    question={q}
                    onChange={handleEditQuestionChange}
                    onRemove={handleEditRemoveQuestion}
                    t={t}
                  />
                ))}
              </div>
            </>
          ) : editError && !editLoading ? (
            <div className="alert alert-danger py-2 small">{editError}</div>
          ) : null}
        </Modal.Body>
        {!editSuccess && !editLoading && editQuiz && (
          <Modal.Footer className="flex-column flex-sm-row gap-2">
            <Button variant="secondary" onClick={handleCloseEditModal} disabled={editSaving} className="order-2 order-sm-1">
              {t.common.cancel || "Abbrechen"}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveEdit}
              disabled={editSaving || !editQuiz.title.trim() || editQuiz.questions.length === 0}
              className="order-1 order-sm-2 d-flex align-items-center gap-2"
            >
              {editSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  {t.liveQuiz.saving || "Speichert..."}
                </>
              ) : (
                <>
                  <BookOpen size={16} />
                  {"Speichern"}
                </>
              )}
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </main>
  );
}
